import { forwardRef, SyntheticEvent, MouseEvent } from 'react';
import classNames from 'classnames';
import classes from './styles/index.module.scss';
import { RouteLinkDirtyElements } from './RouteLinkDirtyElements';
import { RouteLinkElements } from './RouteLinkElements';
import { BaseButtonProps } from '../BaseButton';
import { DirtyFormProxyButtonProps } from '../DirtyFormProxy/types';
import { LinkProps } from '../Link';
import { RouteLinkDomPropsType } from './type';
import { useRouteLinkNavigation } from './useRouteLinkNavigation';
import { isBadOrEmptyString } from '../../predicates/string';
import { HELLOFLEX_ROUTE_LINK_CLASS } from './const';
import { allowNavEvent } from './utils';

type RouteLinkProps = {
	onFocus?: (event: SyntheticEvent) => void;
	onBlur?: (event: SyntheticEvent) => void;
	replace?: boolean;
	useDirty?: boolean;
	forceUseLink?: boolean;
	preventDefault?: boolean;
};

const RouteLink = forwardRef<
	HTMLAnchorElement,
	LinkProps & RouteLinkProps & BaseButtonProps & DirtyFormProxyButtonProps
>(
	(
		{
			onClick,
			styledButton = false,
			disabled,
			target = '_self',
			hasColor = true,
			noUnderline: noUnderlineFromProps = true,
			href,

			onFocus,
			onBlur,
			replace = false,
			useDirty,

			className,
			tagName = 'a',
			tabIndex = 0,
			children,
			scroll = true,
			preventDefault = true,
			forceUseLink,
			...props
		},
		ref,
	) => {
		const routeLinkNavigation = useRouteLinkNavigation();

		const handleComponentClick = (e: React.MouseEvent<HTMLElement, React.MouseEvent>) => {
			if (preventDefault) {
				e.preventDefault();
			}

			if (disabled) {
				return;
			}

			onClick && onClick(e);

			if (isBadOrEmptyString(href) || !allowNavEvent(e)) {
				return;
			}

			const openInNewTab = e.ctrlKey || e.metaKey || target === '_blank';

			routeLinkNavigation({
				href,
				replace,
				scroll,
				openInNewTab,
				forceUseLink,
			});
		};
		const handleComponentFocus = (e: SyntheticEvent) => {
			onFocus && onFocus(e);
		};
		const handleComponentBlur = (e: SyntheticEvent) => {
			onBlur && onBlur(e);
		};
		const handleMouseDown = (e: MouseEvent<HTMLElement, MouseEvent>) => {
			if (e.button !== 1 || disabled || isBadOrEmptyString(href) || tagName === 'a' || !allowNavEvent(e)) {
				return;
			}

			window.open(href, '_blank');
		};

		const shouldRemoveUnderline = noUnderlineFromProps || styledButton;

		const componentClasses: string = classNames(
			'routeLink',
			HELLOFLEX_ROUTE_LINK_CLASS,
			classes.routeLink,
			{
				[classes.routeLink_noUnderline]: shouldRemoveUnderline,
				[classes.routeLink_hasColor]: !styledButton && hasColor,
			},
			className,
		);

		const domProps: RouteLinkDomPropsType = {
			href: href || '#',
			target,
			// @ts-ignore
			onClick: handleComponentClick,
			onFocus: handleComponentFocus,
			onBlur: handleComponentBlur,
			onMouseDown: handleMouseDown,
			rel: target === '_blank' ? 'noreferrer noopener' : undefined,
			tabIndex,
		};

		return useDirty ? (
			<RouteLinkDirtyElements
				{...props}
				ref={ref}
				domProps={domProps}
				tagName={tagName}
				disabled={disabled}
				styledButton={styledButton}
				componentClasses={componentClasses}
			>
				{children}
			</RouteLinkDirtyElements>
		) : (
			<RouteLinkElements
				{...props}
				ref={ref}
				domProps={domProps}
				tagName={tagName}
				disabled={disabled}
				styledButton={styledButton}
				componentClasses={componentClasses}
			>
				{children}
			</RouteLinkElements>
		);
	},
);

RouteLink.displayName = 'RouteLink';

export { RouteLink };
